import React, { SFC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserModel } from '../../../shared/types/user'
import { Grid, CircularProgress, Card, CardContent, Typography, ListItem, TextField, InputAdornment, IconButton, Menu, MenuItem, Badge, Checkbox } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useSelector } from 'react-redux'
import { useCalcHeightToBottomPartialList } from '../../../utils/ui'
import { NoData } from '../../../shared/components/NoData'
import SearchIcon from '@material-ui/icons/Search'
import FilterListIcon from '@material-ui/icons/FilterList'

const useStyles = makeStyles((theme) => ({
    list: {
        overflowY: 'auto',
        marginTop: theme.spacing(3),
    },
    listItem: {
        margin: theme.spacing(1, 1, 2, 1),
        display: 'flex',
        '& .Mui-selected': {
            borderBottom: `3px solid ${theme.palette.primary.main}`,
            paddingBottom: theme.spacing(2) - 3,
        },
    },
    listItemButton: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        padding: theme.spacing(2),
    },
    listContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minWidth: 0,
    },
    listContent: {
        flex: '1 0 auto',
        textAlign: 'left',
        padding: 0,
        minWidth: 0,
        '&:last-child': {
            padding: 0,
        },
    },
    listItemName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    search: {
        paddingLeft: theme.spacing(1),
    },
    progress: {
        textAlign: 'center',
        position: 'relative',
        top: '5px'
    },
    dataInfoWrapper: {
        textAlign: 'center',
        paddingLeft: theme.spacing(1),
    },
    input: {
        backgroundColor: '#f5f5f5', 
        borderRadius: theme.shape.borderRadius, 
        overflow: 'hidden',
        border: '1px solid #ddd',
        '& .MuiFilledInput-root': {
            backgroundColor: '#f5f5f5', 
            borderRadius: theme.shape.borderRadius, 
            paddingBottom: '6px',
            height: '40px',
            boxShadow: 'none',
            '&:before, &:after': {
                display: 'none', 
            },
        },
        '& .MuiFilledInput-input': {
            padding: '17px 12px 10px 0px'
        },
        '& .MuiInputLabel-root': {
            top: '-7px', 
            fontSize: '0.9rem', 
        },
        '& .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)': {
            marginTop: '8px',
        },
    }
}))

interface UserListProps {
    users: UserModel[]
    selectedUser?: UserModel
    search: string
    onSearchChange(value: string): void
    statusFilter: string[]
    onStatusFilterChange(value: string[]): void
    onUserSelected(user: UserModel): void
}

const statusFilterOptions = ['unconfirmed', 'blocked', 'monthlyBilling', 'unpaid']

const getStatusFilterLabel = (option: string, t: (key: string) => string) => {
    switch (option) {
        case 'unconfirmed':
            return t('users.unconfirmedBadge')
        case 'blocked':
            return t('users.blockedBadge')
        case 'monthlyBilling':
            return t('users.monthlyBillingFilter')
        case 'unpaid':
            return t('users.unpaidFilter')
        default:
            return option
    }
}

const UserList: SFC<UserListProps> = ({ users, selectedUser, search, onSearchChange, statusFilter, onStatusFilterChange, onUserSelected }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const usersExecuted = useSelector((state: any) => state.users.items.executed)

    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null)

    const handleSearchChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        onSearchChange(e.target.value)
    }

    const handleFilterIconClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        setFilterAnchorEl(e.currentTarget)
    }

    const handleFilterClose = () => {
        setFilterAnchorEl(null)
    }

    const toggleStatusFilter = (value: string) => {
        if (statusFilter.includes(value)) {
            onStatusFilterChange(statusFilter.filter((x) => x !== value))
        } else {
            onStatusFilterChange([...statusFilter, value])
        }
    }

    const listHeightFill = useCalcHeightToBottomPartialList('listWrapper')

    return (
        <>
            <Grid container>
                <Grid item xs={12} className={classes.search}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                            <TextField
                                fullWidth
                                variant="filled"
                                placeholder={t('users.search')}
                                value={search}
                                onChange={handleSearchChanged}
                                className={classes.input}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>
                        <IconButton onClick={handleFilterIconClick} style={{ marginLeft: 4 }}>
                            <Badge badgeContent={statusFilter.length} color="secondary" invisible={statusFilter.length === 0}>
                                <FilterListIcon style={{ color: statusFilter.length > 0 ? '#6100ED' : undefined }} />
                            </Badge>
                        </IconButton>
                        <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={handleFilterClose}>
                            {statusFilterOptions.map((option) => (
                                <MenuItem key={option} onClick={() => toggleStatusFilter(option)}>
                                    <Checkbox
                                        checked={statusFilter.includes(option)}
                                        style={{ color: '#6100ED', padding: '4px' }}
                                    />
                                    {getStatusFilterLabel(option, t)}
                                </MenuItem>
                            ))}
                        </Menu>
                        <div style={{ width: '32px', textAlign: 'center' }}>
                            {!usersExecuted && <CircularProgress size={24} />}
                        </div>
                    </div>
                </Grid>
                <Grid id="listWrapper" item xs={12} className={classes.list} ref={listHeightFill}>
                    {usersExecuted && users.length === 0 ? (
                        <div className={classes.dataInfoWrapper}>
                            <NoData text={t('users.noUsers')} />
                        </div>
                    ) : (
                        users.map((user) => (
                            <Card key={user.id} className={classes.listItem} elevation={1}>
                                <ListItem
                                    button
                                    selected={user.id === selectedUser?.id}
                                    onClick={() => onUserSelected(user)}
                                    className={classes.listItemButton}
                                >
                                    <div className={classes.listContentWrapper}>
                                        <CardContent className={classes.listContent}>
                                            <Typography variant="h6" noWrap className={classes.listItemName}>
                                                {user.firstName} {user.lastName}
                                            </Typography>
                                            <Typography component="div" variant="caption" noWrap className={classes.listItemName}>
                                                {user.email}
                                            </Typography>
                                            {(!user.confirmed || user.blocked || (user.monthlyAllowance ?? 0) > 0 || (user.unpaidTotal ?? 0) > 0) && (
                                                <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '2px 10px' }}>
                                                    {user.blocked && (
                                                        <Typography variant="caption" style={{ color: '#c62828', fontSize: '11px' }}>
                                                            {t('users.blockedBadge')}
                                                        </Typography>
                                                    )}
                                                    {(user.unpaidTotal ?? 0) > 0 && (
                                                        <Typography variant="caption" style={{ color: '#c62828', fontSize: '11px' }}>
                                                            {t('users.unpaidFilter')}
                                                        </Typography>
                                                    )}
                                                    {!user.confirmed && (
                                                        <Typography variant="caption" style={{ color: '#e65100', fontSize: '11px' }}>
                                                            {t('users.unconfirmedBadge')}
                                                        </Typography>
                                                    )}
                                                    {(user.monthlyAllowance ?? 0) > 0 && (
                                                        <Typography variant="caption" style={{ color: '#6100ED', fontSize: '11px' }}>
                                                            {t('users.monthlyBillingFilter')}
                                                        </Typography>
                                                    )}
                                                </div>
                                            )}
                                        </CardContent>
                                    </div>
                                </ListItem>
                            </Card>
                        ))
                    )}
                </Grid>
            </Grid>
        </>
    )
}

export default UserList